import React from 'react';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';
import { useAgentStore } from '@/stores/agentStore';
import { useAuthStore } from '@/stores/authStore';
import { renderMarkdown } from '@/utils/markdown';
import { sanitizeHtml } from '@/utils/sanitize';
import { formatTime } from '@/utils/formatters';
import { MessageAttachments } from '@/components/common/MessageAttachments';
import { InlineMediaText } from '@/components/common/InlineStepMedia';
import { hasInlineMarker, injectInlineMountPoints, prepareInlineMedia } from '@/utils/inlineMedia';
import { TimelineRow } from './TimelineRow';
import { MsgAvatar } from './MsgAvatar';
import { ProposedReplies, type ProposedReplyActions } from './ProposedReplies';
import type { Message } from '@/types';

/**
 * 2026-11-01 - "<title> (<path>)", or whichever of the two is known; null when neither is. Display text only.
 */
export function customerPageLabel(m: Pick<Message, 'pagePath' | 'pageTitle'>): string | null {
  const title = (m.pageTitle ?? '').trim();
  const path = (m.pagePath ?? '').trim();
  if (title && path) return `${title} (${path})`;
  return title || path || null;
}

/** Render markdown message content to sanitized HTML (XSS-safe). */
function contentHtml(content: string): string {
  return sanitizeHtml(renderMarkdown(content ?? ''));
}

/**
 * A single row of the ONE merged timeline. Sender is CUSTOMER | AI (bot) | AGENT, and `stream` says which
 * conversation the row belongs to: the customer-facing chat (absent / `'chat'`) or the internal bot<->staff
 * consultation (`'consult'`, drawn as a card with an "internal" tag so it can never be mistaken for
 * something the customer sees: red for the bot's note, purple for a staff member's guidance to the bot). Every message has an avatar (see MsgAvatar) and a label that says WHO is
 * talking TO WHOM ("Dana -> customer", "Dana -> bot", "Bot -> agents").
 *
 * The `alert` kind renders a distinct role="alert" box (HC2.2 guidance-delivery-failure) instead of a normal
 * bubble; the `system` kind renders a staff-only chat-timeline line ("Dana took over") between messages.
 */
export const MsgBubble: React.FC<{
  m: Message;
  /** The customer's display name (the person, not the thread label) - the customer avatar's initials. */
  customerName?: string;
  /** Passed through to timeline lines: lets a consultation line switch the filter to Consultation. */
  onOpenConsultation?: () => void;
  /**
   * 2026-10-28 - what the viewer may do with the proposed replies under a bot consultation note. `null`/absent = the
   * viewer may not act on this conversation (closed, read-only, someone else's), and then NO reply is shown at all.
   */
  replyActions?: ProposedReplyActions | null;
}> = ({ m, customerName = '', onOpenConsultation, replyActions = null }) => {
  const { t } = useT();
  const myAgentId = useAuthStore((s) => s.user?.agentId ?? null);
  const directory = useAgentStore((s) => s.directory);

  if (m.kind === 'system' && m.timeline) {
    return <TimelineRow entry={m.timeline} onOpenConsultation={onOpenConsultation} />;
  }

  if (m.kind === 'alert') {
    return (
      <div className="ac-msg-alert" role="alert" data-testid="msg-alert">
        <Icon name="alert" size={16} />
        <span dir="auto">{m.content}</span>
      </div>
    );
  }

  const sender = m.sender; // CUSTOMER | AI | AGENT
  const consult = m.stream === 'consult';
  // 2026-10-28 (operator): a staff member's guidance TO the bot is purple (`guidance`), so it can never be read as
  // the bot's own consultation note, which stays red.
  const guidance = consult && sender === 'AGENT';
  const cls =
    (sender === 'CUSTOMER' ? 'customer' : sender === 'AGENT' ? 'agent out' : 'bot') +
    (consult ? ' consult' : '') +
    (guidance ? ' guidance' : '');
  // Proposed replies are offered only under a BOT consultation note, and only when the caller says this viewer may act.
  const showReplies =
    consult && sender === 'AI' && !!replyActions && Array.isArray(m.proposedReplies) && m.proposedReplies.length > 0;

  // WS-E.R-B1 — an attachment-only message has `content: ''` (the new
  // customer upload flow's common case, not an edge case). `.ac-bubble` is
  // styled with padding + a background colour, so rendering it unconditionally
  // paints a visible empty coloured pill above the attachment row. Only render
  // the bubble when there is actual trimmed text; a message with neither text
  // nor attachments then simply renders no content above its meta line —
  // sane (no dangling empty box), never a crash.
  //
  // 2026-10-15 - a bot (or agent) reply may carry `[[media:K]]` picture markers (K = 1-based position in m.attachments,
  // the contract the widget, WhatsApp and email render). The console used to show them as raw text AND repeat the same
  // pictures in the strip below. Same order as DraftReviewCard: markers become sentinels BEFORE markdown/sanitize (an
  // unplaceable marker - nothing behind it, a non-image, a repeat - is removed), mount points are injected AFTER
  // sanitize, and pictures placed inline leave the strip. Customer text is never rewritten (the widget does the same).
  // A message with no marker takes the untouched path below, byte-for-byte as before.
  const inlineView =
    sender !== 'CUSTOMER' && hasInlineMarker(m.content)
      ? prepareInlineMedia(m.content, m.attachments)
      : { text: m.content ?? '', placed: [] as number[] };
  const stripAttachments =
    inlineView.placed.length > 0
      ? (m.attachments ?? []).filter((_a, i) => !inlineView.placed.includes(i))
      : m.attachments;
  const hasText = inlineView.text.trim().length > 0;

  // OP-1041 - who an AGENT row is. This used to say "You" for EVERY agent row, so after a reload a
  // teammate's reply was labelled as the viewer's own. "You" is now only the signed-in user (or the 'me'
  // sentinel an optimistic local row carries); anyone else is named from the agent directory, and an agent
  // who is not in it (deleted, or the directory fetch failed) is a neutral "an agent" - never "You" and never
  // a raw id.
  const agentId = (m.senderId ?? '').trim();
  const agentLabel =
    agentId === 'me' || (!!myAgentId && agentId === myAgentId)
      ? t('sender_you')
      : (agentId && directory[agentId]) || t('sender_agent_unknown');

  const toLabel = consult ? t('tl_agent_to_bot', { name: agentLabel }) : t('tl_agent_to_customer', { name: agentLabel });

  return (
    <div className={'ac-msg ' + cls} data-testid={`msg-${sender}`} data-stream={consult ? 'consult' : 'chat'}>
      <MsgAvatar m={m} customerName={customerName} sessionId={m.sessionId} />
      <div className="ac-msg-body">
        {consult && (
          <span className="ac-msg-tag" data-testid="msg-consult-tag">
            <Icon name="lock" size={11} />
            {t('tl_consult_internal')}
          </span>
        )}
        {hasText &&
          (inlineView.placed.length > 0 ? (
            <InlineMediaText
              className="ac-bubble"
              html={injectInlineMountPoints(contentHtml(inlineView.text))}
              attachments={m.attachments ?? []}
            />
          ) : (
            <div className="ac-bubble" dir="auto" dangerouslySetInnerHTML={{ __html: contentHtml(inlineView.text) }} />
          ))}
        <MessageAttachments attachments={stripAttachments} />
        <div className="ac-msg-meta">
          {sender === 'AI' && (
            <span className="ac-msg-sender bot">
              <Icon name="bot" size={12} />
              {consult ? t('tl_bot_to_agents') : t('sender_bot')}
            </span>
          )}
          {sender === 'AGENT' && (
            <span className="ac-msg-sender agent" data-testid="msg-agent-label">
              {toLabel}
            </span>
          )}
          {sender !== 'CUSTOMER' && '· '}
          {m.timestamp ? formatTime(m.timestamp) : ''}
          {/*
            OP-681 - the invariant is that no UI reports delivered for a message that was never
            transmitted. This tick used to render unconditionally on every agent bubble, including on
            email and WhatsApp sessions where nothing was delivering the reply at all, so the agent was
            shown a delivery confirmation for a message that reached nobody.

            Deliberately two states, not four. `deliveryFailed === true` is the only statement we can
            make; its absence means "nobody has said", which is why the tick still renders there rather
            than being replaced by a pending indicator. Turning that into pending/sent/delivered/seen is
            the status ladder and belongs to the work that owns it.

            A consultation row was never sent to the customer at all (it is a note to the bot), so it gets
            no delivery tick either way.
          */}
          {sender === 'AGENT' &&
            !consult &&
            (m.deliveryFailed === true ? (
              <span className="ac-msg-undelivered" role="status" data-testid="msg-undelivered">
                <Icon name="alert" size={13} />
                {t('msg_not_delivered')}
              </span>
            ) : (
              <Icon name="checkdouble" size={13} style={{ color: 'var(--accent)' }} />
            ))}
        </div>
        {/*
          2026-11-01 - the page of the tenant's site the customer was on when they sent this message. A CLIENT CLAIM
          (the host page's loader reported it): rendered as plain React text, never as HTML, never as a link.
        */}
        {sender === 'CUSTOMER' && !consult && customerPageLabel(m) && (
          <div className="ac-msg-page" dir="auto" data-testid="msg-customer-page" title={customerPageLabel(m) ?? undefined}>
            {t('msg_customer_page', { page: customerPageLabel(m) ?? '' })}
          </div>
        )}
        {showReplies && replyActions && (
          <ProposedReplies note={m} replies={m.proposedReplies ?? []} actions={replyActions} />
        )}
      </div>
    </div>
  );
};
